/**
 * The configurable options of a plan as staff pick them when quoting or ordering (ADR-032 §2.7):
 * one control per kind. A group left as it is takes its default item (or its minimum quantity) on
 * the server, which checks every choice again (required groups, conditions, bounds). Choices only
 * staff may pick are offered too, marked. A quantity takes digits only (a whole number).
 */
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { OptionGroup } from '../../api/areas/catalog';
import { Field } from '../../components/blocks';
import type { OptionPick } from '../../lib/catalog';
import { selectClass } from '../operator/shared';
import { useCatalogT } from './editorKit';

export function OptionPicker({ groups, picks, onChange }: { groups: readonly OptionGroup[]; picks: Readonly<Record<string, OptionPick>>; onChange: (picks: Record<string, OptionPick>) => void }) {
  const { t, name } = useCatalogT();
  const set = (code: string, p: OptionPick) => onChange({ ...picks, [code]: p });
  return (
    <>
      {groups.map((g) => {
        const items = g.items.filter((i) => i.visibility !== 'RETIRED');
        const label = g.required ? `${name(g)} *` : name(g);
        const p = picks[g.code] ?? {};
        switch (g.kind) {
          case 'QUANTITY':
            return (
              <Field key={g.code} label={label} hint={t('order.quantityHint', { min: g.min_qty ?? 0, max: g.max_qty ?? 0 })}>
                <Input inputMode="numeric" value={p.qty ?? ''} placeholder={String(g.min_qty ?? 0)} onChange={(e) => set(g.code, { qty: e.target.value.replace(/\D/g, '') })} className="w-32 tabular" />
              </Field>
            );
          case 'TEXT':
          case 'SSH_KEY':
            return (
              <Field key={g.code} label={label}>
                <Textarea rows={g.kind === 'SSH_KEY' ? 3 : 1} value={p.text ?? ''} onChange={(e) => set(g.code, { text: e.target.value })} className={g.kind === 'SSH_KEY' ? 'font-mono text-xs' : undefined} />
              </Field>
            );
          case 'CHECKBOX': {
            const item = items[0];
            return item ? (
              <label key={g.code} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={p.item === item.code} onChange={(e) => set(g.code, e.target.checked ? { item: item.code } : {})} />
                {label}
              </label>
            ) : null;
          }
          default: {
            const def = items.find((i) => i.is_default);
            return (
              <Field key={g.code} label={label}>
                <select className={selectClass} value={p.item ?? ''} onChange={(e) => set(g.code, e.target.value ? { item: e.target.value } : {})}>
                  <option value="">{def ? t('order.optionDefault', { item: name(def) }) : t('order.choose')}</option>
                  {items.map((i) => (
                    <option key={i.code} value={i.code}>
                      {i.visibility === 'HIDDEN' ? t('order.optionHidden', { item: name(i) }) : name(i)}
                    </option>
                  ))}
                </select>
              </Field>
            );
          }
        }
      })}
    </>
  );
}
